import { Button, Input, Card, Badge } from "@vlab-corporation/design-system";
import { AppLayout } from "@/components/AppLayout";
import { QueryStateHandler } from "@/components/QueryStateHandler";
import { useMovements } from "@/hooks/useMovements";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import type { MovementCategory } from "@vlab-corporation/vlab-pilates-service-interface/movement";
import type { AuthUser } from "@/types/auth";

const CATEGORIES: { value: MovementCategory | null; label: string }[] = [
  { value: null, label: "전체" },
  { value: "mat", label: "Mat" },
  { value: "reformer", label: "Reformer" },
  { value: "cadillac", label: "Cadillac" },
  { value: "chair", label: "Chair" },
  { value: "barrel", label: "Barrel" },
  { value: "other", label: "기타" },
];

export function MovementCatalogPage() {
  const user = useAuthenticatedUser();
  if (!user) return null;
  return <MovementCatalogPageContent user={user} />;
}

function MovementCatalogPageContent({ user }: { user: AuthUser }) {
  const { centerOwnerUid, centerId } = user.activeMembership;
  const {
    groupedMovements,
    isLoading,
    error,
    refetch,
    category,
    setCategory,
    keyword,
    setKeyword,
  } = useMovements(centerOwnerUid, centerId);

  return (
    <AppLayout>
      <h2 className="text-xl font-semibold mb-4">동작 카탈로그</h2>

      <div className="mb-4">
        <Input
          label="동작 검색"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          placeholder="동작명 또는 설명으로 검색"
        />
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {CATEGORIES.map((cat) => (
          <Button
            key={cat.label}
            variant={category === cat.value ? "primary" : "outline"}
            onClick={() => setCategory(cat.value)}
          >
            {cat.label}
          </Button>
        ))}
      </div>

      <QueryStateHandler
        isLoading={isLoading}
        error={error}
        errorMessage="동작 목록을 불러오지 못했습니다."
        onRetry={() => refetch()}
        isEmpty={groupedMovements.size === 0}
        emptyMessage="검색 결과가 없습니다."
      >
        {[...groupedMovements.entries()].map(([cat, movements]) => (
          <div key={cat} className="mb-6">
            <h3 className="text-sm font-medium text-gray-500 uppercase mb-2">{cat}</h3>
            <div className="space-y-2">
              {movements.map((m) => (
                <Card key={m.id} className="p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-medium">{m.name}</span>
                      {m.description && (
                        <p className="text-xs text-gray-500 mt-0.5">{m.description}</p>
                      )}
                    </div>
                    <Badge variant="info">{cat}</Badge>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        ))}
      </QueryStateHandler>
    </AppLayout>
  );
}
